// Operator › Connections (`/operator/connections`; CONNECTIONS, Andrei 2026-10-25: "configurable
// in the settings so we don't have to change the code every time"): the platform's service
// endpoints — Recall, the LiteLLM gateway, AiGentPanel, the Hermes overlay policy
// (`GET /v1/operator/service-endpoints`). One row per service: the value in force and where it
// comes from (stored here, or the environment variable's default), with visible Test connection,
// Edit and Reset to default buttons. Edit validates on the server; moving a credentialed service
// to another host asks for the new host to be typed back (the server's 409 says why). Secrets are
// never on this page: they stay in the sealed keyring. Platform admins only (the API's 403 is
// shown as a notice).

import type { OperationOutput } from "@companyos/sdk";
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  LocationHeader,
  Notice,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { PlugIcon, ServerIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { problemText } from "../ops-parts";
import { LinkButton, OpsFrame, operatorCrumb } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";
import { EffortDefaultsCard } from "./effort-defaults-card";
import { MemoryLaneCard } from "./memory-lane-card";
import { OpsAccountCard } from "./ops-account-card";

type Endpoint = OperationOutput<"operator.serviceEndpoints.list">["data"][number];
type TestResult = OperationOutput<"operator.serviceEndpoints.test">;
type HostChange = { from_host: string | null; to_host: string | null; tenants: number | null };

const endpointsKey = ["operator", "service-endpoints"] as const;
const endpointsQuery = (api: Api) =>
  queryOptions({
    queryKey: endpointsKey,
    queryFn: () => api.client.operator.serviceEndpoints.list(),
  });

/** The 409 of an unconfirmed host change, when that is what the error is. */
function hostChangeOf(error: unknown): HostChange | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const current = (error.problem as { current?: unknown } | null)?.current;
  return current && typeof current === "object" && "to_host" in current
    ? (current as HostChange)
    : null;
}

function SourceBadge({ e }: { e: Endpoint }) {
  if (e.source === "stored") return <Badge variant="accent">Set here</Badge>;
  if (e.source === "env") return <Badge variant="neutral">Default · {e.env_var}</Badge>;
  return <Badge variant="outline">Not set</Badge>;
}

export function ConnectionsPage({ api }: { api: Api }) {
  const endpoints = useQuery(endpointsQuery(api));
  const forbidden = endpoints.error instanceof ApiError && endpoints.error.status === 403;
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Connections" }]}
        icon={PlugIcon}
        title="Connections"
      />
      <div className="grid gap-4 px-6 py-4 max-md:px-4">
        <Notice tone="info" title="Addresses only">
          The services CompanyOS reaches. A value set here wins over the server's environment
          variable; Reset returns to it. Keys and secrets are not on this page: they stay sealed in
          the server's keyring.
        </Notice>
        {endpoints.isPending ? (
          <Loading label="Loading connections" />
        ) : forbidden ? (
          <EmptyState
            variant="card"
            icon={PlugIcon}
            title="Platform admins only"
            description={problemText(
              endpoints.error,
              "Connections are managed by an admin of the platform's agency.",
            )}
          />
        ) : endpoints.isError ? (
          <LoadError what="Connections" error={endpoints.error} />
        ) : (
          <div className="grid gap-3" data-slot="connections">
            {endpoints.data.data.map((e) => (
              <EndpointCard key={e.key} api={api} e={e} />
            ))}
            <OpsAccountCard api={api} />
            <EffortDefaultsCard api={api} />
            <MemoryLaneCard api={api} />
            <section
              className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-4 py-3"
              data-slot="agent-endpoints-card"
            >
              <ServerIcon className="size-4 text-muted-foreground" aria-hidden />
              <h2 className="text-sm font-semibold">Agent endpoints</h2>
              <span className="text-xs text-muted-foreground">
                Where each agent's Hermes profile answers; keys sealed.
              </span>
              <LinkButton size="xs" className="ml-auto" render={<Link to="/operator/satellites" />}>
                Satellites
              </LinkButton>
            </section>
          </div>
        )}
      </div>
    </OpsFrame>
  );
}

function EndpointCard({ api, e }: { api: Api; e: Endpoint }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const test = useMutation({
    mutationFn: () =>
      api.client.operator.serviceEndpoints.test({ params: { key: e.key }, body: {} }),
  });
  return (
    <section
      className="rounded-lg border border-border bg-card p-4"
      data-endpoint={e.key}
      data-source={e.source}
      aria-label={e.label}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            {e.label} <SourceBadge e={e} />
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">{e.description}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1" data-slot="endpoint-actions">
          {e.testable ? (
            <Button
              variant="secondary"
              size="xs"
              disabled={!e.value || test.isPending}
              onClick={() => test.mutate()}
            >
              {test.isPending ? "Testing…" : "Test connection"}
            </Button>
          ) : null}
          <Button variant="secondary" size="xs" onClick={() => setEditing(true)}>
            Edit
          </Button>
          {e.source === "stored" ? (
            <Button variant="ghost" size="xs" onClick={() => setResetting(true)}>
              Reset to default
            </Button>
          ) : null}
        </div>
      </div>
      <dl className="mt-3 grid gap-1 text-xs sm:grid-cols-[10rem_1fr]">
        <dt className="text-muted-foreground">In force</dt>
        <dd className="min-w-0 break-all" data-slot="endpoint-value">
          {e.value ? (
            <code>{e.value}</code>
          ) : (
            <span className="text-muted-foreground">Not set</span>
          )}
        </dd>
        {e.source === "stored" ? (
          <>
            <dt className="text-muted-foreground">Default ({e.env_var})</dt>
            <dd className="min-w-0 break-all">
              {e.env_value ? (
                <code>{e.env_value}</code>
              ) : (
                <span className="text-muted-foreground">Not set</span>
              )}
            </dd>
            {e.updated_at ? (
              <>
                <dt className="text-muted-foreground">Changed</dt>
                <dd>{formatDateTime(e.updated_at)}</dd>
              </>
            ) : null}
          </>
        ) : null}
        <dt className="text-muted-foreground">Takes effect</dt>
        <dd>{e.applies}</dd>
      </dl>
      {e.problem ? (
        <Notice tone="danger" className="mt-3" title="Off: the value in force does not work">
          {e.problem}
        </Notice>
      ) : null}
      {e.warning ? (
        <Notice tone="warning" className="mt-3" data-slot="endpoint-warning">
          {e.warning}
        </Notice>
      ) : null}
      {test.data ? <TestNotice result={test.data} /> : null}
      {test.error ? (
        <Notice tone="danger" className="mt-3">
          {problemText(test.error, "The test could not run.")}
        </Notice>
      ) : null}
      {/* Mounted per opening: every opening starts from the value in force, with no draft,
          error or typed confirmation left from the last one. */}
      {editing ? (
        <EditDialog
          api={api}
          e={e}
          open
          onOpenChange={setEditing}
          onSaved={() => {
            test.reset();
            void qc.invalidateQueries({ queryKey: endpointsKey });
          }}
        />
      ) : null}
      {resetting ? (
        <ResetDialog
          api={api}
          e={e}
          open
          onOpenChange={setResetting}
          onDone={() => {
            test.reset();
            void qc.invalidateQueries({ queryKey: endpointsKey });
          }}
        />
      ) : null}
    </section>
  );
}

function TestNotice({ result }: { result: TestResult }) {
  return (
    <Notice
      tone={result.ok ? "success" : "warning"}
      className="mt-3"
      data-slot="endpoint-test"
      data-ok={result.ok ? "true" : "false"}
      title={result.ok ? "Reachable" : "Not reachable"}
    >
      {result.detail}
      {result.latency_ms !== null ? ` · ${result.latency_ms} ms` : ""}
      {result.tested_url ? (
        <span className="block text-xs text-muted-foreground">
          GET <code>{result.tested_url}</code>, no key sent, redirects not followed
        </span>
      ) : null}
    </Notice>
  );
}

function ConfirmHost({
  change,
  value,
  onChange,
  id,
}: {
  change: HostChange;
  value: string;
  onChange: (v: string) => void;
  id: string;
}) {
  return (
    <Field data-slot="confirm-host">
      <FieldLabel htmlFor={id}>Type {change.to_host} to confirm the move</FieldLabel>
      <Input
        id={id}
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
      />
      <FieldDescription>
        From {change.from_host ?? "nothing"} to {change.to_host}
        {change.tenants !== null
          ? ` · ${change.tenants} companies have a Recall tenant on the old host`
          : ""}
        . The service's sealed credential will be sent to the new host.
      </FieldDescription>
    </Field>
  );
}

function EditDialog({
  api,
  e,
  open,
  onOpenChange,
  onSaved,
}: {
  api: Api;
  e: Endpoint;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(e.stored_value ?? e.value ?? "");
  const [confirm, setConfirm] = useState("");
  const id = useId();
  const confirmId = useId();
  const save = useMutation({
    mutationFn: () =>
      api.client.operator.serviceEndpoints.set({
        params: { key: e.key },
        body: { value: value.trim(), ...(confirm.trim() ? { confirm_host: confirm.trim() } : {}) },
      }),
    onSuccess: () => {
      onSaved();
      onOpenChange(false);
    },
  });
  const test = useMutation({
    mutationFn: () =>
      api.client.operator.serviceEndpoints.test({
        params: { key: e.key },
        body: { value: value.trim() },
      }),
  });
  const change = hostChangeOf(save.error);
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (value.trim()) save.mutate();
  };
  const hint =
    e.kind === "url"
      ? "A base URL: no user name, password, query or fragment."
      : e.kind === "cidrs"
        ? "Comma-separated IPv4 ranges, e.g. 100.64.0.0/10."
        : e.kind === "model"
          ? "A gateway model name, e.g. deepseek-flash."
          : "Comma-separated IPv4 addresses inside the overlay ranges.";
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) {
          setValue(e.stored_value ?? e.value ?? "");
          setConfirm("");
          save.reset();
          test.reset();
        }
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Edit {e.label}</DialogTitle>
            <DialogDescription>
              Stored here, it wins over {e.env_var}
              {e.env_value ? ` (${e.env_value})` : " (not set)"}. {e.applies}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={id}>{e.kind === "url" ? "URL" : "Value"}</FieldLabel>
            <Input
              id={id}
              autoComplete="off"
              spellCheck={false}
              value={value}
              onChange={(ev) => {
                setValue(ev.target.value);
                test.reset();
              }}
            />
            <FieldDescription>{hint}</FieldDescription>
            {save.error && !change ? (
              <FieldError>{problemText(save.error, "The value could not be saved.")}</FieldError>
            ) : null}
          </Field>
          {change ? (
            <>
              <Notice tone="warning" title="This moves the service to another host">
                {problemText(save.error, "Type the new host to confirm.")}
              </Notice>
              <ConfirmHost change={change} value={confirm} onChange={setConfirm} id={confirmId} />
            </>
          ) : null}
          {test.data ? <TestNotice result={test.data} /> : null}
          {test.error ? (
            <Notice tone="danger">{problemText(test.error, "The test could not run.")}</Notice>
          ) : null}
          <DialogFooter>
            {e.testable ? (
              <Button
                type="button"
                variant="secondary"
                disabled={!value.trim() || test.isPending}
                onClick={() => test.mutate()}
              >
                {test.isPending ? "Testing…" : "Test this URL"}
              </Button>
            ) : null}
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button
              type="submit"
              disabled={save.isPending || !value.trim() || (change !== null && !confirm.trim())}
            >
              {save.isPending ? "Saving…" : change ? "Confirm and save" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetDialog({
  api,
  e,
  open,
  onOpenChange,
  onDone,
}: {
  api: Api;
  e: Endpoint;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const [confirm, setConfirm] = useState("");
  const confirmId = useId();
  const reset = useMutation({
    mutationFn: () =>
      api.client.operator.serviceEndpoints.reset({
        params: { key: e.key },
        body: confirm.trim() ? { confirm_host: confirm.trim() } : {},
      }),
    onSuccess: () => {
      onDone();
      onOpenChange(false);
    },
  });
  const change = hostChangeOf(reset.error);
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) {
          setConfirm("");
          reset.reset();
        }
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <form
          className="grid gap-4"
          onSubmit={(ev) => {
            ev.preventDefault();
            reset.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Reset {e.label} to its default?</DialogTitle>
            <DialogDescription>
              The value set here is cleared and {e.env_var} applies again:{" "}
              {e.env_value ?? "not set, so the service turns off"}.
            </DialogDescription>
          </DialogHeader>
          {change ? (
            <>
              <Notice tone="warning" title="This moves the service to another host">
                {problemText(reset.error, "Type the new host to confirm.")}
              </Notice>
              <ConfirmHost change={change} value={confirm} onChange={setConfirm} id={confirmId} />
            </>
          ) : reset.error ? (
            <Notice tone="danger">
              {problemText(reset.error, "The value could not be reset.")}
            </Notice>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button
              type="submit"
              variant="destructive"
              disabled={reset.isPending || (change !== null && !confirm.trim())}
            >
              {reset.isPending ? "Resetting…" : change ? "Confirm and reset" : "Reset to default"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
